import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ShieldAlert } from 'lucide-react';
import { INDICATIVE_RATE_PCT } from '../lib/finance';
import {
  LEGAL_ENTITY,
  TRADING_NAME,
  CONTACT_EMAIL,
  CONTACT_LOCATION,
  COMMISSION_DISCLOSURE,
  DEALER_COMMISSION_DISCLOSURE,
  MONEYTALKS,
  hasRegulatoryIdentity,
} from '../config/legal';

/**
 * /disclaimer — the page a customer, a lender or a regulator lands on to find out what Levr is,
 * what it is not, and what the numbers on the rest of the site actually mean.
 *
 * Every claim here is one the site can currently stand behind. Deliberately absent: any lender
 * count, any named lender, any rate presented as an offer, and any registration number that has not
 * been supplied. The registration block below renders only the fields that are set in
 * `config/legal.js` — see that file for why they start empty.
 *
 * ⚠️ This is legal-adjacent copy on a consumer credit site. It was drafted to be accurate and
 * conservative, but it has NOT been reviewed by a NZ compliance adviser. Get that review before
 * treating it as sufficient disclosure.
 */

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-outfit text-xl font-semibold text-black mb-3">{title}</h2>
    {children}
  </section>
);

const Disclaimer = () => {
  // Only rows with a real value. A blank line on a page like this reads as carelessness, and an
  // empty "NZBN:" label is worse than not raising the subject.
  const identityRows = [
    ['Registered name', LEGAL_ENTITY.legalName],
    ['NZBN', LEGAL_ENTITY.nzbn],
    ['Financial Service Provider number', LEGAL_ENTITY.fspNumber],
    ['Registered address', LEGAL_ENTITY.registeredAddress],
  ].filter(([, value]) => Boolean(value));

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#00D47E]/10 rounded-full flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-[#00D47E]" />
            </div>
            <h1 className="font-outfit text-3xl md:text-4xl font-bold text-black">Disclaimer</h1>
          </div>
          <p className="text-gray-500 text-sm mb-12">Last updated: August 2026</p>

          <div className="space-y-10 text-gray-700 leading-relaxed">
            <Section title="We are not a lender">
              <p className="mb-3">
                {TRADING_NAME} is a vehicle finance broker and origination platform. We do not lend
                money, we do not issue credit, and we are not a motor vehicle trader. If your
                application proceeds, the credit contract is between you and the lender, on that
                lender&rsquo;s terms — not ours.
              </p>
              <p>
                <strong>
                  All lending decisions, rates, fees and terms are determined by the lender.
                </strong>{' '}
                We can put your situation in front of them and argue your case. We cannot approve
                you, price your loan, or overturn a decline.
              </p>
            </Section>

            <Section title="We are not financial advisers">
              <p className="mb-3">
                Nothing on this website is regulated financial advice, and nothing here takes your
                personal circumstances, goals or wider financial position into account. Checking
                your eligibility sends your details to our team, not to a lender — one of our
                specialists reviews them and, if you decide to go ahead, works with lenders on your
                behalf. We do not advise you on whether borrowing is the right decision for you.
              </p>
              <p>
                If you want genuine, independent financial help, it is available free.{' '}
                <a
                  href={MONEYTALKS.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#00D47E] hover:underline"
                >
                  {MONEYTALKS.name}
                </a>{' '}
                offers free, confidential financial mentoring to anyone in New Zealand — call{' '}
                <strong>{MONEYTALKS.phone}</strong> or visit{' '}
                <a
                  href={MONEYTALKS.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#00D47E] hover:underline"
                >
                  moneytalks.co.nz
                </a>
                . They are independent of us and they do not sell anything.
              </p>
            </Section>

            <Section title="Every figure on this site is indicative">
              <p className="mb-3">
                Repayment figures shown in our calculator and throughout the site are worked out at
                an assumed rate of <strong>{INDICATIVE_RATE_PCT}% p.a.</strong> That is an
                assumption used to do the arithmetic. It is not a rate we are offering you, and it
                is not a rate we can promise any lender will offer you.
              </p>
              <p>
                Real offers depend on your credit history, your income and expenses, the vehicle,
                the term, the deposit and the lender&rsquo;s own assessment. The rates lenders
                actually offer through us range considerably higher than the figure used in the
                calculator. Fees, interest and terms are set by the lender and will be disclosed to
                you in writing before you sign anything.
              </p>
            </Section>

            <Section title="An eligibility check is not an approval">
              <p>
                Our eligibility result is an estimate based only on what you tell us. It is not a
                credit decision, a pre-approval, or an offer of finance, and it does not bind any
                lender. Approval is never guaranteed. Lending criteria, responsible lending checks,
                fees and terms and conditions apply.
              </p>
            </Section>

            <Section title="Checking your eligibility does not affect your credit score">
              <p>
                Our eligibility check does not run a credit check and leaves no mark on your credit
                file. A lender may run a credit check later, if you choose to proceed to a full
                application. We will tell you before that happens.
              </p>
            </Section>

            <Section title="How we are paid">
              {COMMISSION_DISCLOSURE ? <p className="mb-3">{COMMISSION_DISCLOSURE}</p> : null}
              <p>{DEALER_COMMISSION_DISCLOSURE}</p>
            </Section>

            <Section title="Vehicles">
              <p className="mb-3">
                You choose the vehicle; we arrange finance for the one you nominate. We do not
                inspect, sell, supply or warrant vehicles, and we make no representation about the
                condition, history, valuation, roadworthiness or fitness for purpose of any vehicle
                you finance.
              </p>
              <p>
                Before you commit to a used vehicle, we strongly recommend you arrange an
                independent pre-purchase inspection — the{' '}
                <a
                  href="https://www.aa.co.nz/cars/inspections/pre-purchase-inspections/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#00D47E] hover:underline"
                >
                  AA pre-purchase inspection
                </a>{' '}
                is the usual route in New Zealand — and check the vehicle on the Personal Property
                Securities Register for money still owing on it. Both are the buyer&rsquo;s to
                arrange, and both are worth doing before there is a loan attached to the car.
              </p>
            </Section>

            <Section title="Accuracy of this website">
              <p>
                We take care to keep this site accurate and current, but we do not warrant that it
                is free of errors or omissions, and rates, criteria and lender participation change
                without notice. To the maximum extent permitted by law, {TRADING_NAME} accepts no
                liability for any loss arising from reliance on information published here. Nothing
                in this disclaimer limits your rights under the Consumer Guarantees Act 1993, the
                Fair Trading Act 1986, or the Credit Contracts and Consumer Finance Act 2003 where
                those Acts apply.
              </p>
            </Section>

            {identityRows.length > 0 ? (
              <Section title="Who we are">
                <dl className="space-y-2">
                  {identityRows.map(([label, value]) => (
                    <div key={label} className="flex flex-col sm:flex-row sm:gap-2">
                      <dt className="font-semibold text-black sm:min-w-[16rem]">{label}</dt>
                      <dd className="m-0">{value}</dd>
                    </div>
                  ))}
                </dl>
              </Section>
            ) : null}

            <Section title="If something goes wrong">
              {hasRegulatoryIdentity() && LEGAL_ENTITY.disputeScheme ? (
                <p className="mb-3">
                  Talk to us first — most things are quickest to fix directly. If we cannot resolve
                  it, you can escalate free of charge to{' '}
                  <strong>{LEGAL_ENTITY.disputeScheme}</strong>
                  {LEGAL_ENTITY.disputeSchemePhone ? <> on {LEGAL_ENTITY.disputeSchemePhone}</> : null}
                  {LEGAL_ENTITY.disputeSchemeUrl ? (
                    <>
                      {' '}(
                      <a
                        href={LEGAL_ENTITY.disputeSchemeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00D47E] hover:underline"
                      >
                        {LEGAL_ENTITY.disputeSchemeUrl.replace(/^https?:\/\//, '')}
                      </a>
                      )
                    </>
                  ) : null}
                  . They are independent of us and their service is free to you.
                </p>
              ) : (
                <p className="mb-3">
                  Tell us — we would rather hear it. Email{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#00D47E] hover:underline">
                    {CONTACT_EMAIL}
                  </a>{' '}
                  and we will come back to you.
                </p>
              )}
              <p>
                Your complaint about a lender should go to that lender, and to the dispute
                resolution scheme they belong to — they are required to tell you which one that is.
              </p>
            </Section>

            <Section title="Contact">
              <p>
                <strong>Email:</strong>{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#00D47E] hover:underline">
                  {CONTACT_EMAIL}
                </a>
                <br />
                <strong>Location:</strong> {CONTACT_LOCATION}
              </p>
              <p className="mt-3">
                See also our{' '}
                <a href="/privacy" className="text-[#00D47E] hover:underline">Privacy Policy</a>
                {' '}and{' '}
                <a href="/terms" className="text-[#00D47E] hover:underline">Terms of Service</a>.
              </p>
            </Section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Disclaimer;
